import { create } from 'zustand';
import type {
  Faction,
  FactionNpc,
  FactionQuestHook,
  FactionReputation,
  CharacterIdeology,
  ReputationTier,
} from '@machina/core';
import { GRIST_FACTIONS, GRIST_NPCS, GRIST_QUEST_HOOKS, getReputationTier } from '@machina/core';
import { trpc } from '../lib/trpc';
import { createMultiSubscription } from '../lib/realtimeHelpers';

/**
 * Faction Store
 *
 * Manages faction lore, reputation tracking, and ideology state.
 * Static lore (factions, NPCs, quest hooks) comes from @machina/core.
 * Dynamic state (reputation scores, ideology positions) syncs via Supabase.
 */

interface ReputationWithTier extends FactionReputation {
  tier: ReputationTier;
}

interface FactionState {
  // Static lore (from core)
  factions: Faction[];
  npcs: FactionNpc[];
  questHooks: FactionQuestHook[];

  // Dynamic state (from Supabase via API)
  reputations: ReputationWithTier[];
  ideologies: CharacterIdeology[];
  availableQuests: FactionQuestHook[];

  // Loading
  isLoading: boolean;
  error: string | null;

  // Realtime
  realtimeCleanup: (() => void) | null;

  // Actions — Load
  loadLore: () => Promise<void>;
  loadReputations: (campaignId: string) => Promise<void>;
  loadIdeologies: (campaignId: string) => Promise<void>;
  loadAvailableQuests: (campaignId: string) => Promise<void>;
  loadAll: (campaignId: string) => Promise<void>;

  // Actions — Mutations
  updateReputation: (
    campaignId: string,
    factionId: string,
    delta: number,
    event: string,
    characterId?: string
  ) => Promise<void>;
  shiftIdeology: (
    campaignId: string,
    characterId: string,
    orderEntropyDelta: number,
    selfCollectiveDelta: number,
    action: string
  ) => Promise<void>;

  // Realtime
  subscribeToRealtime: (campaignId: string) => void;
  unsubscribeFromRealtime: () => void;

  // Queries
  getReputationForFaction: (factionId: string) => ReputationWithTier | undefined;
  getIdeologyForCharacter: (characterId: string) => CharacterIdeology | undefined;
}

export const useFactionStore = create<FactionState>((set, get) => ({
  factions: GRIST_FACTIONS,
  npcs: GRIST_NPCS,
  questHooks: GRIST_QUEST_HOOKS,
  reputations: [],
  ideologies: [],
  availableQuests: [],
  isLoading: false,
  error: null,
  realtimeCleanup: null,

  // ==========================================================================
  // LOAD
  // ==========================================================================

  loadLore: async () => {
    try {
      const lore = await trpc.factions.getLore.query();
      set({
        factions: lore.factions,
        npcs: lore.npcs,
        questHooks: lore.questHooks,
      });
    } catch (err) {
      console.error('[FactionStore] Error loading lore:', err);
      // Fall back to core constants (already set as defaults)
    }
  },

  loadReputations: async (campaignId: string) => {
    try {
      const reps = await trpc.factions.getReputations.query({ campaignId });
      set({ reputations: reps as ReputationWithTier[] });
    } catch (err) {
      console.error('[FactionStore] Error loading reputations:', err);
      set({ error: 'Failed to load faction reputations' });
    }
  },

  loadIdeologies: async (campaignId: string) => {
    try {
      const ideologies = await trpc.factions.getIdeologies.query({ campaignId });
      set({ ideologies });
    } catch (err) {
      console.error('[FactionStore] Error loading ideologies:', err);
      set({ error: 'Failed to load character ideologies' });
    }
  },

  loadAvailableQuests: async (campaignId: string) => {
    try {
      const quests = await trpc.factions.getQuestHooks.query({ campaignId });
      set({ availableQuests: quests });
    } catch (err) {
      console.error('[FactionStore] Error loading quests:', err);
    }
  },

  loadAll: async (campaignId: string) => {
    set({ isLoading: true, error: null });
    try {
      await Promise.all([
        get().loadLore(),
        get().loadReputations(campaignId),
        get().loadIdeologies(campaignId),
        get().loadAvailableQuests(campaignId),
      ]);
    } finally {
      set({ isLoading: false });
    }
  },

  // ==========================================================================
  // MUTATIONS
  // ==========================================================================

  updateReputation: async (campaignId, factionId, delta, event, characterId) => {
    try {
      const result = await trpc.factions.updateReputation.mutate({
        campaignId,
        factionId,
        characterId: characterId || 'party',
        delta,
        event,
      });

      // Update local state
      const { factions } = get();
      const faction = factions.find((f) => f.id === factionId);
      const thresholds = faction?.reputationThresholds || {
        hostile: -60,
        unfriendly: -20,
        friendly: 20,
        allied: 60,
      };
      const withTier: ReputationWithTier = {
        ...result,
        tier: getReputationTier(result.reputationScore, thresholds),
      };

      set((state) => {
        const idx = state.reputations.findIndex(
          (r) => r.factionId === factionId && r.characterId === (characterId || 'party')
        );
        const newReps = [...state.reputations];
        if (idx >= 0) {
          newReps[idx] = withTier;
        } else {
          newReps.push(withTier);
        }
        return { reputations: newReps };
      });
    } catch (err) {
      console.error('[FactionStore] Error updating reputation:', err);
      set({ error: 'Failed to update reputation' });
    }
  },

  shiftIdeology: async (campaignId, characterId, orderEntropyDelta, selfCollectiveDelta, action) => {
    try {
      const result = await trpc.factions.shiftIdeology.mutate({
        campaignId,
        characterId,
        orderEntropyDelta,
        selfCollectiveDelta,
        action,
      });

      set((state) => {
        const idx = state.ideologies.findIndex((i) => i.characterId === characterId);
        const newIdeologies = [...state.ideologies];
        if (idx >= 0) {
          newIdeologies[idx] = result;
        } else {
          newIdeologies.push(result);
        }
        return { ideologies: newIdeologies };
      });
    } catch (err) {
      console.error('[FactionStore] Error shifting ideology:', err);
      set({ error: 'Failed to shift ideology' });
    }
  },

  // ==========================================================================
  // REALTIME
  // ==========================================================================

  subscribeToRealtime: (campaignId: string) => {
    get().unsubscribeFromRealtime();

    const { cleanup } = createMultiSubscription({
      channelName: `factions-${campaignId}`,
      tables: [
        {
          table: 'machina_faction_reputation',
          filter: `campaign_id=eq.${campaignId}`,
          event: '*',
          onPayload: () => get().loadReputations(campaignId),
        },
        {
          table: 'machina_character_ideology',
          filter: `campaign_id=eq.${campaignId}`,
          event: '*',
          onPayload: () => get().loadIdeologies(campaignId),
        },
      ],
    });

    set({ realtimeCleanup: cleanup });
  },

  unsubscribeFromRealtime: () => {
    const { realtimeCleanup } = get();
    if (realtimeCleanup) {
      realtimeCleanup();
      set({ realtimeCleanup: null });
    }
  },

  // ==========================================================================
  // QUERIES
  // ==========================================================================

  getReputationForFaction: (factionId: string) => {
    return get().reputations.find((r) => r.factionId === factionId && r.characterId === 'party');
  },

  getIdeologyForCharacter: (characterId: string) => {
    return get().ideologies.find((i) => i.characterId === characterId);
  },
}));
